import type { ReactNode } from 'react';
import {
  ApprovalReason,
  ApprovalReasonLabels,
  ApprovalReasonTones,
  ApprovalStatus,
  ApprovalStatusLabels,
  ApprovalStatusTones,
  DocumentStatus,
  DocumentStatusLabels,
  DocumentStatusTones,
  GatewayScenario,
  GatewayScenarioLabels,
  IntakeChannel,
  IntakeChannelLabels,
  QboConnectionStatus,
  QboConnectionStatusLabels,
  QboConnectionStatusTones,
  QboEntityType,
  QboEntityTypeLabels,
  RuleAction,
  RuleActionLabels,
  RuleActionTones,
  SyncDirection,
  SyncDirectionLabels,
  SyncOutcome,
  SyncOutcomeLabels,
  SyncOutcomeTones,
} from '../lib/enums';
import type { ChipTone } from '../lib/enums';
import { formatPercent, toPercent } from '../lib/format';

export function Chip({
  tone = 'neutral',
  children,
  title,
}: {
  tone?: ChipTone;
  children: ReactNode;
  title?: string;
}): JSX.Element {
  return (
    <span className={`chip chip-${tone}`} title={title}>
      {children}
    </span>
  );
}

export function StatusChip({ status }: { status: DocumentStatus }): JSX.Element {
  return <Chip tone={DocumentStatusTones[status]}>{DocumentStatusLabels[status]}</Chip>;
}

export function ScenarioChip({ scenario }: { scenario: GatewayScenario }): JSX.Element {
  return <Chip tone="neutral">{GatewayScenarioLabels[scenario]}</Chip>;
}

export function IntakeChip({ channel }: { channel: IntakeChannel }): JSX.Element {
  return <Chip tone="neutral">{IntakeChannelLabels[channel]}</Chip>;
}

export function EntityTypeChip({ entityType }: { entityType: QboEntityType }): JSX.Element {
  return <Chip tone="neutral">{QboEntityTypeLabels[entityType]}</Chip>;
}

export function OutcomeChip({ outcome }: { outcome: SyncOutcome }): JSX.Element {
  return <Chip tone={SyncOutcomeTones[outcome]}>{SyncOutcomeLabels[outcome]}</Chip>;
}

export function DirectionChip({ direction }: { direction: SyncDirection }): JSX.Element {
  return (
    <Chip tone={direction === SyncDirection.Push ? 'info' : 'neutral'}>
      {SyncDirectionLabels[direction]}
    </Chip>
  );
}

export function ApprovalStatusChip({ status }: { status: ApprovalStatus }): JSX.Element {
  return <Chip tone={ApprovalStatusTones[status]}>{ApprovalStatusLabels[status]}</Chip>;
}

export function ApprovalReasonChip({
  reason,
  detail,
}: {
  reason: ApprovalReason;
  detail?: string | null;
}): JSX.Element {
  return (
    <Chip tone={ApprovalReasonTones[reason]} title={detail ?? undefined}>
      {ApprovalReasonLabels[reason]}
    </Chip>
  );
}

export function RuleActionChip({ action }: { action: RuleAction }): JSX.Element {
  return <Chip tone={RuleActionTones[action]}>{RuleActionLabels[action]}</Chip>;
}

export function ConnectionStatusChip({ status }: { status: QboConnectionStatus }): JSX.Element {
  return <Chip tone={QboConnectionStatusTones[status]}>{QboConnectionStatusLabels[status]}</Chip>;
}

/** Confidence expressed as a coloured badge. Accepts 0-1 fractions or 0-100 percentages. */
export function ConfidenceBadge({
  value,
  label,
  size = 'md',
}: {
  value: number | null | undefined;
  label?: string;
  size?: 'sm' | 'md';
}): JSX.Element {
  const pct = toPercent(value);
  if (pct === null) {
    return (
      <span className={`confidence confidence-none confidence-${size}`} title="No confidence score">
        —
      </span>
    );
  }
  const tone = pct >= 90 ? 'good' : pct >= 70 ? 'warn' : 'bad';
  return (
    <span
      className={`confidence confidence-${tone} confidence-${size}`}
      title={label ? `${label}: ${formatPercent(value, 1)}` : `Confidence ${formatPercent(value, 1)}`}
    >
      {formatPercent(value)}
    </span>
  );
}

/** Small dot used for health indicators. */
export function HealthDot({ ok, title }: { ok: boolean; title?: string }): JSX.Element {
  return (
    <span
      className={`health-dot ${ok ? 'health-ok' : 'health-bad'}`}
      title={title}
      role="img"
      aria-label={title ?? (ok ? 'Healthy' : 'Unhealthy')}
    />
  );
}
